import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import initializeMailRibbonConfiguration from '../mutators/initializeMailRibbonConfigurationMutator';
import { defaultConfig } from 'owa-mail-ribbon-store-shared-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyRibbonVersionHandler } from 'owa-mail-ribbon-versioning';
import { updateUserConfigurationAndService } from 'owa-session-store/lib/utils/updateUserConfigurationAndService';
import type { MailRibbonConfigStore } from 'owa-mail-ribbon-store-shared-types';
import { ribbonValidityCheck } from './ribbonValidityCheck';

/**
 * Load the appropriate mail ribbon configuration for the user.
 * This function calls owa-session-store's @see {getUserConfiguration} to pull in the
 * user's persisted ribbon. If they have never persisted before, it'll be an empty string.
 */
export default async function loadMailRibbonConfiguration() {
    if (isFeatureEnabled('mon-ribbon-customization-hard-reset')) {
        hardResetRibbon();
        initializeMailRibbonConfiguration(defaultConfig);
        return;
    }

    const ribbonConfigJSON = getUserConfiguration().ViewStateConfiguration?.MailRibbonConfig;
    let ribbonConfig;

    if (!isFeatureEnabled('mon-ribbon-customization') || !ribbonConfigJSON) {
        // Default scenario of an uncustomized ribbon
        ribbonConfig = defaultConfig;
    } else {
        // User has a customized ribbon.
        ribbonConfig = JSON.parse(ribbonConfigJSON!);

        // First, do a validity check to ensure that the user's ribbon is able to be rendered given
        // current app state and version.
        if (ribbonValidityCheck(ribbonConfig)) {
            // Parse the ribbonConfigJSON but use defaultConfig's implementation for certain tabs.
            // MLR and Format Text Tab are uncustomizable and therefore will always use the default version.
            // When turning on customization for these tabs, will need to use versioning to ensure all users
            // are on the same version. Likely a `removeTab()` then `addTab()` call.
            ribbonConfig.multiline = defaultConfig.multiline;
            ribbonConfig.singleline.formatTab = defaultConfig.singleline.formatTab;
            ribbonConfig.singleline.insertTab = defaultConfig.singleline.insertTab;

            // Check if versions are mismatched, in which case handle versioning
            if (ribbonConfig.version != defaultConfig.version) {
                const ribbonVersionHandler = await lazyRibbonVersionHandler.import();
                ribbonVersionHandler(ribbonConfig);
            }
        } else {
            // Bad ribbon state, return defaultConfig.
            ribbonConfig = defaultConfig;
        }
    }

    initializeMailRibbonConfiguration(ribbonConfig as MailRibbonConfigStore);
}

/**
 * If the flight `mon-ribbon-customization-hard-reset` is turned on, then upon load
 * the ribbon will return to the default non-customizable state.
 * This is just in case versioning causes the ribbon to become in a bad state.
 */
function hardResetRibbon() {
    const ribbonConfigString: string = JSON.stringify(defaultConfig);
    updateUserConfigurationAndService(
        config => {
            if (config.ViewStateConfiguration) {
                config.ViewStateConfiguration.MailRibbonConfig = ribbonConfigString;
            }
        },
        [{ key: 'MailRibbonConfig', valuetype: 'String', value: [ribbonConfigString] }],
        'OWA.ViewStateConfiguration'
    );
}
